import React, { FC, useEffect, useState, useMemo } from 'react';
import s from './AppointmentBookingBundle.module.scss';
import clsx from 'clsx';
import ProfileMenuHeader from 'modules/app/components/ProfileMenu/ProfileMenuHeader';
import ChooseServices from './ChooseServices';
import SelectProfessional from './SelectProfessional';
import { useTypedSelector } from 'store/useTypedSelector';
import { useActions } from 'store/useActions';
import BookingCheckoutMulti from './BookingCheckout/BookingCheckoutMulti';
import useUpdateMultiAvailabilityTime from 'sdk/hooks/booking/useUpdateMultiAvailabilityTime';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from 'sdk/api';
import useIsAuth from 'sdk/hooks/useIsAuth';
import ChooseLocation from 'components/shared/ChooseLocation';
import OrdersList from './OrdersList';
import Steps from './Steps';
import useGetServicePricingDurationByVideo from 'sdk/hooks/booking/useGetServicePricingDurationByVideo';
import { useTranslation } from 'react-i18next';
import Page from 'modules/app/components/Page';
import useGetBundleById from 'sdk/hooks/salon/useGetBundleById';
import BookingCalendar from 'components/shared/BookingCalendar';
import PaymentOption from '../AppointmentBookingSpec/PaymentOption';

export enum AppointmentBookingSteps {
  CHOOSE_SERVICES = 'Choose services',
  CHOOSE_SERVICES_BOOK = 'Choose services book',
  SELECT_THE_PROFESSIONAL = 'Select the professional',
  CHOOSE_DATE = 'Choose date',
  CHOOSE_PAYMENT_OPTION = 'Choose payment option',
  CHECKOUT = 'Booking checkout',
  VOUCHER_EMAIL = 'Voucher email',
  VOUCHER_TIME = 'Voucher time',
  VOUCHER_RESULT = 'Voucher result',
  CHOOSE_LOCATION = 'Choose location',
  ORDERS_LIST = 'Orders list',
}

interface SelectedDay {
  year: number;
  month: number;
  day: number;
}

type Props = {};

const AppointmentBooking: FC<Props> = (): JSX.Element => {
  const navigate = useNavigate();
  const { isAppointmentBookingOpen } = useTypedSelector(state => state.postSlice);
  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const [selectedLocation, setSelectedLocation] = useState(null);
  const { salonId, bundleId } = useParams();
  const { t } = useTranslation() as any;
  const { data: bundle, refetch: refetchBunle }: any = useGetBundleById(bundleId);
  const { mutate, mutateAsync } = useUpdateMultiAvailabilityTime();

  const [selectedProf, setSelectedProf] = useState(null);
  const [currentServiceId, setCurrentServiceId] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [selectedDay, setSelectedDay] = useState<SelectedDay | null>(null);
  const [paymentMethod, setPaymentMethod] = useState(null);

  const services = useMemo(() => bundle?.service_bundle_items?.map(service => service.service_id), [bundle]);
  const locationType = bundle?.service_bundle_items?.map(
    service => service?.services?.service_location,
  )?.[0];
  const [selectedServices, setSelectedServices] = useState<any>([]);
  const [homeLocation, setHomeLocation] = useState({
    street: '',
    apartment: '',
    floor: '',
    entrance: '',
    note: '',
    intercom: '',
    name: '',
    phone: '',
  });

  const [bookingTransactions, setBookingTransactions] = useState<any>([]);
  const [selectedServiceHistory, setSelectedServiceHistory] = useState<any>([]);
  const [bookingItemsWithDiscount, setBookingItemsWithDiscount] = useState([]);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  
  const [step, setStep] = useState(AppointmentBookingSteps.CHOOSE_LOCATION);
  const isChooseServices = AppointmentBookingSteps.CHOOSE_SERVICES;
  const isSelectProfessional = AppointmentBookingSteps.SELECT_THE_PROFESSIONAL;
  const isChooseDate = AppointmentBookingSteps.CHOOSE_DATE;
  const isChoosePaymentOption = AppointmentBookingSteps.CHOOSE_PAYMENT_OPTION;
  const isCheckout = AppointmentBookingSteps.CHECKOUT;
  const isChooseLocation = AppointmentBookingSteps.CHOOSE_LOCATION;
  const isOrdersList = AppointmentBookingSteps.ORDERS_LIST;

  const handleCloseMenu = (): void => {
    if (step === isChooseLocation) {
      navigate(-1);
      return;
    }

    if (step === isSelectProfessional) {
      setSelectedProf(null);
      setBookingTransactions([]);
      setStep(isChooseLocation);
      return;
    }

    if (step === isChooseDate) {
      debugger;
      setSelectedTime(null);
      setSelectedEndTime(null);
      setSelectedDay(null);
      setStep(isSelectProfessional);
      if (selectedServices.length === 0) {
        setSelectedServices([selectedServiceHistory[selectedServiceHistory.length - 1]]);
        return;
      }
      setSelectedServices(services);
      return;
    }

    if (step === isChoosePaymentOption) {
      setPaymentMethod(null);
      setBookingTransactions(prev => {
        return prev.slice(0, -1);
      });
      setSelectedTime(null);
      setSelectedDay(null);
      setStep(isChooseDate);
      return;
    }

    if (step === isCheckout) {
      setStep(isChooseDate);
      return;
    }

    if (step === isOrdersList) {
      setStep(isChoosePaymentOption);
      return;
    }
  };

  const settingAppointments = (): void => {
    // Проверяем, нет ли уже такой записи
    const isDuplicate = bookingTransactions.some(
      transaction =>
        transaction.team_id === selectedProf &&
        (selectedDay
          ? transaction.date ===
            `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(
              selectedDay.day,
            ).padStart(2, '0')}`
          : false) &&
        transaction.start_time === selectedTime &&
        transaction.service === currentServiceId,
    );

    if (isDuplicate) {
      return; // Прерываем выполнение функции если найден дубликат
    }

    setBookingTransactions(prev => {
      return [
        ...prev,
        {
          order_id: 'Offline',
          team_id: selectedProf,
          start_time: selectedTime,
          end_time: selectedEndTime,
          duration: bundle?.duration,
          //@ts-ignore
          date: selectedDay
            ? `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(
                selectedDay.day,
              ).padStart(2, '0')}`
            : '',
          status: 'Booked',
          user_id: isAuthData?.user?.id,
          price: bundle?.price,
          service: currentServiceId,
        },
      ];
    });
    setSelectedTime(null);
    setSelectedDay(null);
  };

  const handleDeleteBooking = index => {
    const newBookingTransactions = [...bookingTransactions];
    const newSelectedServiceHistory = [...selectedServiceHistory];

    newBookingTransactions.splice(index, 1);
    newSelectedServiceHistory.splice(index, 1);

    setBookingTransactions(newBookingTransactions);
    setSelectedServiceHistory(newSelectedServiceHistory);
  };

  useEffect(() => {
    refetchBunle();
  }, []);

  useEffect(() => {
    if (services) {
      setSelectedServices(services);
    }
  }, [services]);



  return (
    <div className={clsx(s.appointment_booking)}>
      <ProfileMenuHeader text={'appointments_bookings'} onClose={handleCloseMenu} />
      {step !== isOrdersList && step !== isCheckout && <Steps step={step} />}
      {step === isChooseLocation && (
        <ChooseLocation
          handleNext={() => {
            refetchBunle();
            setStep(AppointmentBookingSteps.SELECT_THE_PROFESSIONAL);
          }}
          homeLocation={homeLocation}
          setHomeLocation={setHomeLocation}
          setSelectedLocation={setSelectedLocation}
          locationType={locationType}
        />
      )}
      {step === isSelectProfessional && (
        <SelectProfessional
          selectedService={[selectedServices?.[0]]}
          selectedId={selectedProf}
          handleChoose={setSelectedProf}
          handleNext={() => {
            const firstService = selectedServices[0];
            setCurrentServiceId(typeof firstService === 'object' ? firstService.id : firstService);
            setSelectedServiceHistory(selectedServices);
            setSelectedServices(selectedServices.slice(1));
            setStep(AppointmentBookingSteps.CHOOSE_DATE);
          }}
        />
      )}
      {step === isChooseDate && (
        <BookingCalendar
          teamId={selectedProf}
          salonId={salonId}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          setSelectedEndTime={setSelectedEndTime}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          handleNext={() => {
            if (selectedServices.length === 0) {
              settingAppointments();
              // setStep(AppointmentBookingSteps.CHOOSE_PAYMENT_OPTION)
              setStep(AppointmentBookingSteps.CHOOSE_PAYMENT_OPTION);
            } else {
              settingAppointments();
              setStep(AppointmentBookingSteps.SELECT_THE_PROFESSIONAL);
            }
          }}
          duration={bundle?.duration}
          selectedServices={selectedServices}
          selectedServiceHistory={selectedServiceHistory}
          currentServiceId={currentServiceId}
          alreadyBookedTimes={bookingTransactions}
        />
      )}

      {step === isOrdersList && (
        <OrdersList
          handleNext={() => setStep(AppointmentBookingSteps.CHECKOUT)}
          selectedServiceHistory={services}
          bookingTransactions={bookingTransactions}
          onDelete={handleDeleteBooking}
          price={bundle?.price}
        />
      )}
      {step === isChoosePaymentOption && (
        <PaymentOption
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          serviceId={selectedServiceHistory}
          isLoading={isSubmittingBooking}
          handleNext={() => {
            if (paymentMethod === 'online') {
              setStep(AppointmentBookingSteps.ORDERS_LIST);
            } else {
              if (isSubmittingBooking) return;
              setIsSubmittingBooking(true);
              
              const generatedNumber = Math.floor(100000 + Math.random() * 900000);
              (async () => {
                const results = await Promise.all(
                  bookingTransactions.map(transaction =>
                    mutateAsync({
                      teamId: transaction.team_id,
                      date: transaction.date,
                      startTime: transaction.start_time,
                      duration: transaction.duration,
                      record_id: generatedNumber,
                    }).catch(error => {
                      return null;
                    }),
                  ),
                );

                const transactionsToInsert = bookingTransactions.map((transaction, index) => ({
                  team_id: transaction.team_id,
                  date: transaction.date,
                  duration: transaction.duration,
                  order_id: 'Offline',
                  status: 'Booked',
                  user_id: userId,
                  price: transaction.price,
                  start_time: transaction.start_time,
                  end_time: transaction.end_time,
                  service: transaction.service,
                  home_address: homeLocation,
                  salon_id: salonId,
                  record_id: generatedNumber,
                }));

                const { data: transactions, error } = await supabase
                  .from('booking_transactions')
                  .insert(transactionsToInsert)
                  .select('*');

                if (error) {
                  setIsSubmittingBooking(false);
                  return;
                }
                
                try {
                  await supabase.functions.invoke('send-booking-email', {
                    body: { booking_transaction_id: transactions[0]?.id },
                  });
                } catch (emailError) {
                  console.error('Failed to send booking email:', emailError);
                }

                setIsSubmittingBooking(false);
                navigate(`/booking/${transactions[0]?.id}`);
              })();
            }
          }}
        />
      )}
      {step === isCheckout && (
        <BookingCheckoutMulti
          price={bundle?.price}
          teamId={selectedProf}
          bookingTransactions={bookingTransactions}
          duration={bundle?.duration}
          selectedTime={selectedTime}
          selectedEndTime={selectedEndTime}
          date={
            selectedDay
              ? `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(
                  selectedDay.day,
                ).padStart(2, '0')}`
              : ''
          }
          selectedServiceHistory={services}
          homeLocation={homeLocation}
          salonId={salonId}
        />
      )}
    </div>
  );
};

export default AppointmentBooking;
